import { Check, Copy, Users } from "lucide-react"
import { useEffect, useMemo, useState } from "react"

import type { GuildWarCandidatePoolMember } from "@/core/guild-war/types"

import { Button } from "@/components/button"
import { EmptyState } from "@/components/shared/empty-state"
import { StatusAlert } from "@/components/shared/status-alert"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { SkeletonTable } from "@/components/ui/skeleton"
import {
  formatCandidatePrimaryPathLabel,
  formatCandidateSecondaryPathLabel,
} from "@/lib/utils/candidate-pool-utils"

export function CandidatePoolResultsCard({
  hasData,
  isLoading,
  isError,
  totalCandidates,
  candidateItems,
  page,
  totalPages,
  onPrevPage,
  onNextPage,
}: {
  hasData: boolean
  isLoading: boolean
  isError: boolean
  totalCandidates: number
  candidateItems: GuildWarCandidatePoolMember[]
  page: number
  totalPages: number
  onPrevPage: () => void
  onNextPage: () => void
  onRefetch: () => void
}) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [bulkMessage, setBulkMessage] = useState<string | null>(null)

  const visibleIds = useMemo(
    () => new Set(candidateItems.map((candidate) => candidate.memberId)),
    [candidateItems],
  )

  useEffect(() => {
    setSelectedIds((previous) => {
      const next = new Set(
        Array.from(previous).filter((memberId) => visibleIds.has(memberId)),
      )
      return next.size === previous.size ? previous : next
    })
  }, [visibleIds])

  const allVisibleSelected =
    candidateItems.length > 0 &&
    candidateItems.every((candidate) => selectedIds.has(candidate.memberId))

  const selectedCount = selectedIds.size

  const handleCopySelectedIds = async () => {
    if (selectedIds.size === 0) {
      return
    }

    const payload = Array.from(selectedIds).join("\n")

    try {
      await navigator.clipboard.writeText(payload)
      setBulkMessage(
        `Copied ${selectedIds.size} member ID${selectedIds.size === 1 ? "" : "s"}.`,
      )
    } catch {
      setBulkMessage("Unable to copy selected IDs from this browser context.")
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Results</CardTitle>
        <CardDescription>
          {hasData
            ? `${totalCandidates} candidates found`
            : "Waiting for week selection"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? <SkeletonTable rows={6} columns={7} /> : null}
        {isError ? (
          <StatusAlert tone="danger">
            Failed to load candidate pool.
          </StatusAlert>
        ) : null}

        {hasData ? (
          <>
            {candidateItems.length > 0 ? (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-border/60 bg-card/35 px-3 py-2">
                <div className="text-sm text-muted-foreground">
                  {selectedCount > 0
                    ? `${selectedCount} selected on this page`
                    : "Select candidates for bulk actions"}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedIds(
                        allVisibleSelected
                          ? new Set()
                          : new Set(
                              candidateItems.map(
                                (candidate) => candidate.memberId,
                              ),
                            ),
                      )
                      setBulkMessage(null)
                    }}
                  >
                    {allVisibleSelected ? "Clear visible" : "Select visible"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={selectedCount === 0}
                    onClick={() => {
                      void handleCopySelectedIds()
                    }}
                  >
                    <Copy className="size-3.5" />
                    Copy IDs
                  </Button>
                </div>
              </div>
            ) : null}

            {bulkMessage ? (
              <div className="mb-3 inline-flex items-center gap-1.5 rounded-lg border border-success/35 bg-success/12 px-2.5 py-1 text-xs text-success">
                <Check className="size-3.5" />
                {bulkMessage}
              </div>
            ) : null}

            {candidateItems.length === 0 ? (
              <EmptyState
                icon={<Users className="size-5" />}
                title="No matching candidates"
                description="No guild members match the current filters for this week."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table text-left text-sm">
                  <caption className="sr-only">
                    Candidate pool results with source, availability, skill
                    paths, and absence state
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col" className="w-10">
                        <Checkbox
                          checked={allVisibleSelected}
                          onCheckedChange={(checked) => {
                            setSelectedIds(
                              checked
                                ? new Set(
                                    candidateItems.map(
                                      (candidate) => candidate.memberId,
                                    ),
                                  )
                                : new Set(),
                            )
                            setBulkMessage(null)
                          }}
                          aria-label="Select all visible candidates"
                        />
                      </th>
                      <th scope="col">Member</th>
                      <th scope="col">Source</th>
                      <th scope="col">Availability</th>
                      <th scope="col">Primary</th>
                      <th scope="col">Secondary</th>
                      <th scope="col">Absence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidateItems.map((candidate) => (
                      <tr key={candidate.memberId}>
                        <td>
                          <Checkbox
                            checked={selectedIds.has(candidate.memberId)}
                            onCheckedChange={(checked) => {
                              setSelectedIds((previous) => {
                                const next = new Set(previous)
                                if (checked) {
                                  next.add(candidate.memberId)
                                } else {
                                  next.delete(candidate.memberId)
                                }
                                return next
                              })
                              setBulkMessage(null)
                            }}
                            aria-label={`Select ${candidate.guildDisplayName}`}
                          />
                        </td>
                        <th
                          scope="row"
                          className="max-w-56 truncate font-medium"
                        >
                          {candidate.guildDisplayName}
                        </th>
                        <td>{candidate.source}</td>
                        <td>{candidate.availability}</td>
                        <td>{formatCandidatePrimaryPathLabel(candidate)}</td>
                        <td>{formatCandidateSecondaryPathLabel(candidate)}</td>
                        <td>
                          {candidate.absenceState}
                          {candidate.absenceDays
                            ? ` (${candidate.absenceDays})`
                            : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-3 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={onPrevPage}
                >
                  Prev
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={onNextPage}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  )
}
